import React, { useRef, useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { useHistory, useLocation } from 'react-router-dom';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import SettingsIcon from '@material-ui/icons/Settings';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import HomeIcon from '@material-ui/icons/Home';
import TuneIcon from '@material-ui/icons/Tune';
import { getAllSuppliers } from '../additional/shared/supplierApiHelpers';
import { getProductFilterMaster } from '../additional/shared/productFilterApiHelpers';

const useStyles = makeStyles((theme) => ({
  drawer: {
    position: "absolute",
    left: 0,
    top: 64,
    bottom: 0,
    width: 74,
    backgroundColor: "#f8f9fa",
    borderRight: "1px solid #e0e0e0",
    transition: "all 0.3s ease",
    zIndex: 1200,
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
  },
  drawerContent: {
    padding: theme.spacing(2, 1),
    flex: 1,
  },
  sectionTitle: {
    fontSize: "13px",
    fontWeight: 400,
    color: "#666",
    marginBottom: theme.spacing(2),
    paddingLeft: theme.spacing(1),
  },
  menuList: {
    listStyle: "none",
    padding: 0,
    margin: 0,
  },
  menuItem: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(1, 1),
    margin: theme.spacing(1, 0),
    borderRadius: 8,
    cursor: "pointer",
    transition: "all 0.2s ease",
    position: "relative",
    backgroundColor: "transparent",
    "&:hover": {
      backgroundColor: "#f0f0f0",
    },
  },
  menuItemActive: {
    backgroundColor: "transparent",
    borderLeft: "2px solid #3f51b5",
  },
  menuIcon: {
    width: 32,
    height: 32,
    marginBottom: theme.spacing(1),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#666",
    '& svg': {
      fontSize: 24,
    },
  },
  menuIconActive: {
    color: "#3f51b5",
    '& svg': {
      fontSize: 24,
    },
  },
  menuText: {
    fontSize: "11px",
    fontWeight: 500,
    textAlign: "center",
    color: "#666",
    lineHeight: 1.2,
  },
  menuTextActive: {
    color: "#3f51b5",
    fontWeight: 600,
  },
  
  rightPanel: {
    position: "absolute",
    left: 74,
    top: 64,
    bottom: 0,
    width: 250,
    backgroundColor: "white",
    borderRight: "1px solid #e0e0e0",
    zIndex: 1100,
    overflowY: "auto",
    boxShadow: "2px 0 4px rgba(0,0,0,0.05)",
    display: "flex",
    flexDirection: "column",
  },
  rightPanelContent: {
    padding: theme.spacing(3, 2),
    flex: 1,
  },
  subMenuList: {
    listStyle: "none",
    padding: 0,
    margin: 0,
  },
  subMenuItem: {
    padding: theme.spacing(1.5, 2),
    margin: theme.spacing(0.5, 0),
    borderRadius: 6,
    cursor: "pointer",
    transition: "all 0.2s ease",
    fontSize: "14px",
    color: "#555",
    fontWeight: 400,
    display: "flex",
    alignItems: "center",
    "&:hover": {
      backgroundColor: "#f8f9fa",
    },
  },
  subMenuItemActive: {
    backgroundColor: "#f0f8f0",
    color: "#3f51b5",
    fontWeight: 500,
    "&:hover": {
      backgroundColor: "#e8f5e8",
    },
  },
  blueDot: {
    width: 6,
    height: 6,
    backgroundColor: "#3f51b5",
    borderRadius: "50%",
    marginRight: theme.spacing(1.5),
    flexShrink: 0,
  },
}));

const Sidebar = ({
  isMainDrawerOpen,
  setIsMainDrawerOpen,
  onMenuItemClick = () => {},
  onSubMenuItemClick = () => {},
  handlePublishHistory = () => {},
  handleDeliveryPayment = () => {},
  handleSellerLocation = () => {},
  handleFulfilmentLocation = () => {},
  logout = () => {},
}) => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const sidebarRef = useRef(null);
  const [activeItem, setActiveItem] = useState('Home');
  const [activeSubItem, setActiveSubItem] = useState('Dashboard');

  const menuItems = [
    {
      id: 1,
      name: 'Home',
      icon: <HomeIcon/>,
      subItems: [
        {
          name: 'Dashboard',
          route: '/mapper/country'
        }
      ]
    },
    {
      id: 2,
      name: 'Location',
      icon: <LocationOnIcon/>,
      subItems: [
        {
          name: 'Seller Location',
          route: '/location/sellerLocation'
        },
        {
          name: 'Fullfilment Location',
          route: '/location/fulfillmentLocation'
        },
        {
          name: 'Last Mile SLA',
          route: '/location/lastmileSla'
        }
      ]
    },
    {
      id: 3,
      name: 'Settings',
      icon: <SettingsIcon/>,
      subItems: [
        {
          name: 'Publish Activity',
          route: '/publish/activity'
        },
        {
          name: 'Jobs Activity',
          route: '/jobs/activity'
        },
        {
          name: 'Choose Payment Method',
          route: '/delivery/payment'
        },
        {
          name: 'Log out',
          route: '/logout'
        }
      ]
    },
    {
      id: 4,
      name: 'Imports',
      icon: <CloudUploadIcon/>,
      subItems: [
        {
          name: 'Imports',
          route: '/imports'
        },
        {
          name: 'Imports History',
          route: '/imports/history'
        }
        
      ]
    },
    {
      id: 5,
      name: 'SLA Settings',
      icon: <TuneIcon/>,
      subItems: [
        {
          name: 'Fast Delivery Exclusions',
          route: '/additional'
        },
        {
          name: 'SLA Rules',
          route: '/sla/rules'
        },
        {
          name: 'Option Overrides',
          route: '/sla/option-overrides'
        }
      ]
    }
  ];

  // Update active states based on current route
  useEffect(() => {
    const currentPath = location.pathname;
    
    // Special handling for root path
    if (currentPath === '/') {
      setActiveItem('Home');
      setActiveSubItem('Dashboard');
      return;
    }
    
    // Find menu item and sub-item matching current route
    for (const menuItem of menuItems) {
      const matchingSubItem = menuItem.subItems.find(subItem => 
        currentPath === subItem.route || 
        (currentPath.startsWith(subItem.route) && subItem.route !== '/')
      );
      
      if (matchingSubItem) {
        setActiveItem(menuItem.name);
        setActiveSubItem(matchingSubItem.name);
        break;
      }
    }
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const mainDrawer = sidebarRef.current;
      const menuButton = event.target.closest('.drawer-toggle-button');
      const rightPanel = document.querySelector('.right-panel');

      if (
        mainDrawer &&
        !mainDrawer.contains(event.target) &&
        (!rightPanel || !rightPanel.contains(event.target)) &&
        !menuButton &&
        isMainDrawerOpen
      ) {
        setIsMainDrawerOpen(false);
      }
    };

    if (isMainDrawerOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [setIsMainDrawerOpen]);

  const handleMenuItemClick = (itemName) => {
    const selectedItem = menuItems.find(item => item.name === itemName);
    if (selectedItem && selectedItem.subItems.length > 0) {
      setActiveItem(itemName);
      const firstSubItem = selectedItem.subItems[0];
      setActiveSubItem(firstSubItem.name);
      setIsMainDrawerOpen(true);

      // Warm the supplier list cache when the SLA Settings section is opened so the
      // seller multi-select (used on Fast Delivery Exclusions + SLA Rules) is ready.
      if (itemName === 'SLA Settings') {
        getAllSuppliers().catch((err) => {
          console.error('Supplier list preload failed', err);
        });
        getProductFilterMaster().catch((err) => {
          console.error('Product filter master preload failed', err);
        });
      }

      // Navigate to first sub-item route if it exists and isn't logout
      if (firstSubItem.route && firstSubItem.route !== '/logout') {
        history.push(firstSubItem.route);
      }
    }
  };

  const getActiveSubItems = () => {
    const activeMenuItem = menuItems.find(item => item.name === activeItem);
    return activeMenuItem ? activeMenuItem.subItems : [];
  };

  // Helper function to check if sub-item is active based on current route
  const isSubItemActive = (subItem) => {
    return location.pathname === subItem.route || 
           location.pathname.startsWith(subItem.route) ||
           activeSubItem === subItem.name;
  };

  const handleSubMenuItemClick = (subItemName) => {
    // Find the parent menu item and its route
    const parentMenuItem = menuItems.find(item => 
      item.subItems.some(subItem => subItem.name === subItemName)
    );
    
    const subItem = menuItems
      .flatMap(item => item.subItems)
      .find(subItem => subItem.name === subItemName);
    
    if (parentMenuItem && subItem) {
      setActiveItem(parentMenuItem.name);
      setActiveSubItem(subItemName);
      
      // Navigate to the route if found and not logout
      if (subItem.route && subItem.route !== '/logout') {
        history.push(subItem.route);
      }
    }
    
    // Handle special actions
    switch(subItemName) {
      case 'Publish Activity':
        handlePublishHistory();
        break;
      case 'Choose Payment Method':
        handleDeliveryPayment();
        break;
      case 'Seller Location':
        handleSellerLocation();
        break;
      case 'Fullfilment Location':
        handleFulfilmentLocation();
        break;
      case 'Log out':
        logout();
        break;
      default:
        break;
    }
  };

  return (
    <>
        <div ref={sidebarRef} className={`main-drawer ${classes.drawer}`}>
          <div className={classes.drawerContent}>
            
            <ul className={classes.menuList}>
              {menuItems.map((item) => (
                <li
                  key={item.id}
                  className={`${classes.menuItem} ${
                    activeItem === item.name ? classes.menuItemActive : ''
                  }`}
                  onClick={() => handleMenuItemClick(item.name)}
                >
                  <div className={`${classes.menuIcon} ${
                    activeItem === item.name ? classes.menuIconActive : ''
                  }`}>
                    {item.icon}
                  </div>
                  <span className={`${classes.menuText} ${
                    activeItem === item.name ? classes.menuTextActive : ''
                  }`}>
                    {item.name}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>

      {isMainDrawerOpen && (
        <div className={`right-panel ${classes.rightPanel}`}>
          <div className={classes.rightPanelContent}>
            <ul className={classes.subMenuList}>
              {getActiveSubItems().map((subItem, index) => (
                <li
                  key={index}
                  className={`${classes.subMenuItem} ${
                    isSubItemActive(subItem) ? classes.subMenuItemActive : ''
                  }`}
                  onClick={() => handleSubMenuItemClick(subItem.name)}
                >
                  {isSubItemActive(subItem) && (
                    <span className={classes.blueDot}></span>
                  )}
                  {!isSubItemActive(subItem) && (
                    <span style={{ width: 18, flexShrink: 0 }}></span>
                  )}
                  {subItem.name}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </>
  );
};

export default Sidebar;